import { Button } from '@artic/ui/button';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EditProjectDialog } from './edit-project-dialog';
import type { ProjectPlanningDetail } from './types';

interface EditProjectButtonProps {
  project: ProjectPlanningDetail;
}

/**
 * "Update Project" action + dialog pair. Enabled for draft projects and for
 * projects in `redrafting` — a stored `project_status` value (ENG-830), not a
 * derived one. Otherwise disabled with a native-title tooltip explaining the
 * reason.
 *
 * ENG-720 renders it inline with the breadcrumb (Band 1) via the route's
 * `breadcrumbBadges` resolver, so it reads as a link rather than a button:
 * "Update" — not "Edit" — because committing a change is what the project
 * lifecycle calls a release.
 */
export function EditProjectButton({ project }: EditProjectButtonProps) {
  const { t } = useTranslation('project');
  const [open, setOpen] = useState(false);
  const isDraft = project.project_status === 'draft';
  const canEdit = isDraft || project.project_status === 'redrafting';

  return (
    <>
      <Button
        variant="link"
        size="sm"
        className="h-auto p-0"
        onClick={() => setOpen(true)}
        disabled={!canEdit}
        title={canEdit ? undefined : t('header.updateProjectDisabled')}
      >
        {t('header.updateProject')}
      </Button>
      <EditProjectDialog open={open} onOpenChange={setOpen} project={project} />
    </>
  );
}
